/**
 * Route Configuration
 * Centralized route constants for the application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',

  // Shared tools
  SVG_EDITOR: '/svg-editor',
  HTML_EDITOR: '/html-editor',
  MATH_CONTENT_CABINET: '/math-content-cabinet',
  
  // Auth routes
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    VERIFY_EMAIL: '/verify-email',
  },
  
  // Student routes
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    EXAMS: '/student/exams',
    EXAM_DETAIL: '/student/exams/:examId',
    TAKE_EXAM: '/student/exams/:examId/take',
    RESULTS: '/student/results',
    RESULT_DETAIL: '/student/results/:attemptId',
    PROGRESS: '/student/progress',
    MATHS_NOTES: '/student/learning/maths-notes',
    MATHS_NOTES_TOPIC: '/student/learning/maths-notes/:sectionId/:topicId',
    MATHS_WORKBOOKS: '/student/learning/maths-workbooks',
    MATHS_WORKEXAMPLES: '/student/learning/maths-workexamples',
    MATHS_THEOREMS: '/student/learning/maths-theorems',
    HKDSE_ICT: '/student/learning/hkdse-ict',
    HKDSE_ICT_TOPIC: '/student/learning/hkdse-ict/:topicSlug',
    HKDSE_ICT_NOTES: '/student/learning/hkdse-ict-notes',
    HKDSE_ICT_NOTES_TOPIC: '/student/learning/hkdse-ict-notes/:topicSlug',
    HKDSE_ICT_YEARS: '/student/learning/hkdse-ict-years',
    HKDSE_ICT_YEARS_TOPIC: '/student/learning/hkdse-ict-years/:topicSlug',
    IGCSE: '/student/learning/igcse',
    IGCSE_TOPIC: '/student/learning/igcse/:topicSlug',
    YEAR5_MATHS: '/student/learning/year-5-maths',
    YEAR5_MATHS_GAMES: '/student/learning/year-5-maths/games',
    YEAR5_MATHS_TOPIC: '/student/learning/year-5-maths/:topicSlug',
    PROFILE: '/student/profile',
    SETTINGS: '/student/settings',
  },
  
  // Teacher routes
  TEACHER: {
    DASHBOARD: '/teacher/dashboard',
    EXAMS: '/teacher/exams',
    EXAM_CREATE: '/teacher/exams/create',
    EXAM_EDIT: '/teacher/exams/:examId/edit',
    EXAM_DETAIL: '/teacher/exams/:examId',
    EXAM_ANALYTICS: '/teacher/exams/:examId/analytics',
    QUESTIONS: '/teacher/questions',
    QUESTION_CREATE: '/teacher/questions/create',
    QUESTION_EDIT: '/teacher/questions/:questionId/edit',
    CLASSES: '/teacher/classes',
    CLASS_DETAIL: '/teacher/classes/:classId',
    GRADING: '/teacher/grading',
    ANALYTICS: '/teacher/analytics',
    PROFILE: '/teacher/profile',
    SETTINGS: '/teacher/settings',
  },
  
  // Admin routes
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: '/admin/users/:userId',
    EXAMS: '/admin/exams',
    ORGANIZATIONS: '/admin/organizations',
    ORGANIZATION_DETAIL: '/admin/organizations/:orgId',
    SCHOOLS: '/admin/schools',
    SCHOOL_DETAIL: '/admin/schools/:tenantId',
    SCHOOL_EDIT: '/admin/schools/:tenantId/edit',
    SCHOOL_ANALYTICS: '/admin/schools/:tenantId/analytics',
    SAAS_DATA: '/admin/saas-data',
    STUDENT_VIEW: '/admin/students/:studentId',
    TEACHER_VIEW: '/admin/teachers/:teacherId',
    SECURITY: '/admin/security',
    SETTINGS: '/admin/settings',
    REPORTS: '/admin/reports',
    AUDIT_LOG: '/admin/audit-log',
  },
  
  // Staff routes
  STAFF: {
    DASHBOARD: '/staff/dashboard',
    TEACHERS: '/staff/teachers',
    TEACHER_VIEW: '/staff/teachers/:teacherId',
    STUDENTS: '/staff/students',
    STUDENT_VIEW: '/staff/students/:studentId',
    CLASSES: '/staff/classes',
    CLASS_DETAIL: '/staff/classes/:classId',
    EXAMS: '/staff/exams',
    ANALYTICS: '/staff/analytics',
    SETTINGS: '/staff/settings',
  },
  
  // Parent routes
  PARENT: {
    DASHBOARD: '/parent/dashboard',
    CHILDREN: '/parent/children',
    CHILD_RESULTS: '/parent/children/:studentId/results',
    SETTINGS: '/parent/settings',
  },
  
  // Error routes
  ERRORS: {
    NOT_FOUND: '/404',
    UNAUTHORIZED: '/401',
    FORBIDDEN: '/403',
    SERVER_ERROR: '/500',
  },
};

/**
 * Helper function to generate dynamic routes
 */
export const generateRoute = (route, params = {}) => {
  let path = route;
  Object.keys(params).forEach((key) => {
    path = path.replace(`:${key}`, params[key]);
  });
  return path;
};

/**
 * Route groups for navigation
 */
export const NAV_GROUPS = {
  STUDENT: [
    { path: ROUTES.STUDENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STUDENT.EXAMS, label: 'Exams', icon: 'FileText' },
    { path: ROUTES.SVG_EDITOR, label: 'SVG Editor', icon: 'FileCode2' },
    { path: ROUTES.HTML_EDITOR, label: 'HTML Editor', icon: 'Code2' },
    { path: ROUTES.MATH_CONTENT_CABINET, label: 'File Cabinet', icon: 'Archive' },
    { path: ROUTES.STUDENT.MATHS_NOTES, label: 'Maths Notes', icon: 'BookOpen' },
    { path: ROUTES.STUDENT.MATHS_WORKBOOKS, label: 'Maths WorkBooks', icon: 'BookOpenCheck' },
    { path: ROUTES.STUDENT.MATHS_WORKEXAMPLES, label: 'Maths WorkExamples', icon: 'BookMarked' },
    { path: ROUTES.STUDENT.MATHS_THEOREMS, label: 'Maths Theorems', icon: 'Shapes' },
    { path: ROUTES.STUDENT.HKDSE_ICT, label: 'HKDSE ICT', icon: 'Monitor' },
    { path: ROUTES.STUDENT.HKDSE_ICT_NOTES, label: 'HKDSE ICT Notes', icon: 'StickyNote' },
    { path: ROUTES.STUDENT.HKDSE_ICT_YEARS, label: 'HKDSE ICT By Years', icon: 'CalendarDays' },
    { path: ROUTES.STUDENT.IGCSE, label: 'iGCSE', icon: 'Globe2' },
    { path: ROUTES.STUDENT.YEAR5_MATHS, label: 'Year 5 Maths', icon: 'Calculator' },
    { path: ROUTES.STUDENT.YEAR5_MATHS_GAMES, label: 'Year 5 Maths Games', icon: 'Gamepad2' },
    { path: ROUTES.STUDENT.RESULTS, label: 'Results', icon: 'Award' },
    { path: ROUTES.STUDENT.PROGRESS, label: 'Progress', icon: 'TrendingUp' },
    { path: ROUTES.STUDENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  TEACHER: [
    { path: ROUTES.TEACHER.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.TEACHER.EXAMS, label: 'My Exams', icon: 'FileText' },
    { path: ROUTES.TEACHER.QUESTIONS, label: 'Question Bank', icon: 'HelpCircle' },
    { path: ROUTES.SVG_EDITOR, label: 'SVG Editor', icon: 'FileCode2' },
    { path: ROUTES.HTML_EDITOR, label: 'HTML Editor', icon: 'Code2' },
    { path: ROUTES.MATH_CONTENT_CABINET, label: 'File Cabinet', icon: 'Archive' },
    { path: ROUTES.TEACHER.CLASSES, label: 'Classes', icon: 'Users' },
    { path: ROUTES.TEACHER.GRADING, label: 'Grading', icon: 'ClipboardCheck' },
    { path: ROUTES.TEACHER.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.TEACHER.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  ADMIN: [
    { path: ROUTES.ADMIN.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.ADMIN.SCHOOLS, label: 'Schools', icon: 'Building2' },
    { path: ROUTES.ADMIN.SAAS_DATA, label: 'SaaS Data', icon: 'Database' },
    { path: ROUTES.ADMIN.USERS, label: 'Users', icon: 'Users' },
    { path: ROUTES.ADMIN.EXAMS, label: 'All Exams', icon: 'FileText' },
    { path: ROUTES.SVG_EDITOR, label: 'SVG Editor', icon: 'FileCode2' },
    { path: ROUTES.HTML_EDITOR, label: 'HTML Editor', icon: 'Code2' },
    { path: ROUTES.MATH_CONTENT_CABINET, label: 'File Cabinet', icon: 'Archive' },
    { path: ROUTES.ADMIN.SECURITY, label: 'Security', icon: 'Shield' },
    { path: ROUTES.ADMIN.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  STAFF: [
    { path: ROUTES.STAFF.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STAFF.TEACHERS, label: 'Teachers', icon: 'Users' },
    { path: ROUTES.STAFF.STUDENTS, label: 'Students', icon: 'GraduationCap' },
    { path: ROUTES.STAFF.CLASSES, label: 'Classes', icon: 'BookOpen' },
    { path: ROUTES.STAFF.EXAMS, label: 'Exams', icon: 'FileText' },
    { path: ROUTES.SVG_EDITOR, label: 'SVG Editor', icon: 'FileCode2' },
    { path: ROUTES.HTML_EDITOR, label: 'HTML Editor', icon: 'Code2' },
    { path: ROUTES.MATH_CONTENT_CABINET, label: 'File Cabinet', icon: 'Archive' },
    { path: ROUTES.STAFF.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.STAFF.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  PARENT: [
    { path: ROUTES.PARENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.PARENT.CHILDREN, label: 'My Children', icon: 'Users' },
    { path: ROUTES.SVG_EDITOR, label: 'SVG Editor', icon: 'FileCode2' },
    { path: ROUTES.HTML_EDITOR, label: 'HTML Editor', icon: 'Code2' },
    { path: ROUTES.MATH_CONTENT_CABINET, label: 'File Cabinet', icon: 'Archive' },
    { path: ROUTES.PARENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
};

export default ROUTES;
